import { getActivePinia } from 'pinia';

export type SectionProgressWrite = Record<string, unknown>;
export type SectionProgressSender = (
  data: SectionProgressWrite,
) => Promise<void>;

/**
 * Serialises the progress PUTs of ONE user section: at most one request in
 * flight; writes made meanwhile coalesce into one pending write (field-wise
 * merge, the latest value of each field wins), sent when the in-flight one
 * settles. A write is never retried; `send` reports its own failures.
 *
 * Callers update the local repo first, so navigation never has to wait for
 * the PUT; the queue only guarantees the server sees the writes in order.
 */
export function createSectionProgressWriteQueue() {
  let pending: {
    data: SectionProgressWrite;
    send: SectionProgressSender;
    waiters: Array<() => void>;
  } | null = null;
  let inFlight = false;
  let flushing = 0;
  let idleWaiters: Array<() => void> = [];

  const isIdle = () => !inFlight && !pending && !flushing;

  function notifyIdle() {
    if (!isIdle()) return;
    const waiters = idleWaiters;
    idleWaiters = [];
    waiters.forEach((resolve) => resolve());
  }

  function dispatch(current: NonNullable<typeof pending>) {
    let request: Promise<void>;
    try {
      request = current.send(current.data);
    } catch (error) {
      request = Promise.reject(
        error instanceof Error ? error : new Error(String(error)),
      );
    }
    // A failure must not block the writes behind it.
    return request.catch(() => undefined);
  }

  function pump() {
    if (inFlight) return;
    if (!pending) {
      notifyIdle();
      return;
    }
    const current = pending;
    pending = null;
    inFlight = true;
    void dispatch(current).then(() => {
      inFlight = false;
      current.waiters.forEach((resolve) => resolve());
      pump();
    });
  }

  /**
   * Queue `data` (sent now when idle, else after the in-flight write). Resolves
   * once the request carrying it has settled, whatever the outcome.
   */
  function enqueue(
    data: SectionProgressWrite,
    send: SectionProgressSender,
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      pending = {
        data: { ...pending?.data, ...data },
        send,
        waiters: [...(pending?.waiters ?? []), resolve],
      };
      pump();
    });
  }

  /** Resolves once nothing is in flight or waiting. */
  function whenIdle(): Promise<void> {
    if (isIdle()) return Promise.resolve();
    return new Promise<void>((resolve) => idleWaiters.push(resolve));
  }

  /**
   * The page is going away: a write waiting behind the in-flight one would be
   * lost with the page, so it is sent right now (best effort, out of order —
   * it carries the newest state).
   */
  function flush() {
    if (!pending || !inFlight) return;
    const current = pending;
    pending = null;
    flushing++;
    void dispatch(current).then(() => {
      flushing--;
      current.waiters.forEach((resolve) => resolve());
      notifyIdle();
    });
  }

  return { enqueue, whenIdle, flush, isIdle };
}

export type SectionProgressWriteQueue = ReturnType<
  typeof createSectionProgressWriteQueue
>;

// One queue per user section, shared by every view (the section list unmounts
// while its write is still in flight). Scoped to the pinia instance, which
// owns the local copy these writes mirror.
const noPinia = {};
const registry = new WeakMap<object, Map<string, SectionProgressWriteQueue>>();

export function sectionProgressWriteQueue(
  userSectionDocId: string,
): SectionProgressWriteQueue {
  const owner = getActivePinia() ?? noPinia;
  let queues = registry.get(owner);
  if (!queues) {
    queues = new Map();
    registry.set(owner, queues);
  }
  let queue = queues.get(userSectionDocId);
  if (!queue) {
    queue = createSectionProgressWriteQueue();
    queues.set(userSectionDocId, queue);
    if (typeof window !== 'undefined')
      window.addEventListener('pagehide', queue.flush);
  }
  return queue;
}
